import { FitnessCenter as FitnessCenterIcon } from "@mui/icons-material";
import { Box, Typography } from "@mui/material";
import { useCallback, useMemo, useState } from "react";

import { activityApi } from "apis";
import CustomTable, { CustomTableColumn } from "components/common/CustomTable";
import { BodyCompositionChart } from "components/features/patient/detail";
import TimeRangeSelector from "components/features/patient/detail/chart-components/TimeRangeSelector";
import { formatDateTime } from "helpers/dateTimeHelper";
import { TimeRange } from "helpers/dateRangeHelpers";
import { usePatientActivity } from "hooks/usePatientActivity";
import {
  MetricValueResponseDto,
  MetricValuesPaginationRequestDtoHealthProviderCodeEnum,
  MetricValuesPaginationRequestDtoMetricCodeEnum,
  MetricValuesPaginationRequestDtoOrderByEnum,
} from "libs/cellera-sdk";

export interface BodyCompositionRow {
  weight?: MetricValueResponseDto;
  bodyFatPercentage?: MetricValueResponseDto;
  bmi?: MetricValueResponseDto;
  leanBodyMass?: MetricValueResponseDto;
  recordedAt: Date;
}

export const BODY_COMPOSITION_QUERY_KEY = "activity-body-composition";

interface BodyCompositionTabContentProps {
  patientId: string;
}

const chartColors = ["#3B82F6", "#F97316", "#10B981", "#8B5CF6"];

const BodyCompositionTabContent: React.FC<BodyCompositionTabContentProps> = ({
  patientId,
}) => {
  const [selectedTimeRange, setSelectedTimeRange] = useState<TimeRange>("1W");

  const columns: CustomTableColumn<BodyCompositionRow>[] = useMemo(
    () => [
      {
        id: "weight",
        label: "Weight",
        getValue: (row) => {
          const m = row.weight;
          const value = m?.valueFloat ?? m?.valueInt ?? m?.valueText;
          return value == null ? "-" : `${value} lb`;
        },
        width: "20%",
      },
      {
        id: "bodyFatPercentage",
        label: "Body Fat Percentage",
        getValue: (row) => {
          const m = row.bodyFatPercentage;
          const value = m?.valueFloat ?? m?.valueInt ?? m?.valueText;
          return value == null ? "-" : `${value} %`;
        },
        width: "20%",
      },
      {
        id: "bmi",
        label: "BMI",
        getValue: (row) => {
          const m = row.bmi;
          const value = m?.valueFloat ?? m?.valueInt ?? m?.valueText;
          return value == null ? "-" : `${value}`;
        },
        width: "20%",
      },
      {
        id: "leanBodyMass",
        label: "Lean Body Mass",
        getValue: (row) => {
          const m = row.leanBodyMass;
          const value = m?.valueFloat ?? m?.valueInt ?? m?.valueText;
          return value == null ? "-" : `${value} lb`;
        },
        width: "20%",
      },
      {
        id: "recordedAt",
        label: "Recorded Time",
        getValue: (row) => formatDateTime(row.recordedAt),
        width: "20%",
      },
    ],
    [],
  );

  const fetchPage = useCallback(
    async (page: number, pageSize: number) => {
      const [weightRes, bodyFatRes, bmiRes, leanMassRes] = await Promise.all([
        activityApi.getMetricValuesPaginatedByPatientId({
          patientId,
          metricValuesPaginationRequestDto: {
            page: page + 1,
            pageSize,
            metricCode: MetricValuesPaginationRequestDtoMetricCodeEnum.Weight,
            healthProviderCode:
              MetricValuesPaginationRequestDtoHealthProviderCodeEnum.AppleHealthkit,
            orderBy: MetricValuesPaginationRequestDtoOrderByEnum.Desc,
          },
        }),
        activityApi.getMetricValuesPaginatedByPatientId({
          patientId,
          metricValuesPaginationRequestDto: {
            page: page + 1,
            pageSize,
            metricCode:
              MetricValuesPaginationRequestDtoMetricCodeEnum.BodyFatPercentage,
            healthProviderCode:
              MetricValuesPaginationRequestDtoHealthProviderCodeEnum.AppleHealthkit,
            orderBy: MetricValuesPaginationRequestDtoOrderByEnum.Desc,
          },
        }),
        activityApi.getMetricValuesPaginatedByPatientId({
          patientId,
          metricValuesPaginationRequestDto: {
            page: page + 1,
            pageSize,
            metricCode:
              MetricValuesPaginationRequestDtoMetricCodeEnum.BodyMassIndex,
            healthProviderCode:
              MetricValuesPaginationRequestDtoHealthProviderCodeEnum.AppleHealthkit,
            orderBy: MetricValuesPaginationRequestDtoOrderByEnum.Desc,
          },
        }),
        activityApi.getMetricValuesPaginatedByPatientId({
          patientId,
          metricValuesPaginationRequestDto: {
            page: page + 1,
            pageSize,
            metricCode:
              MetricValuesPaginationRequestDtoMetricCodeEnum.LeanBodyMass,
            healthProviderCode:
              MetricValuesPaginationRequestDtoHealthProviderCodeEnum.AppleHealthkit,
            orderBy: MetricValuesPaginationRequestDtoOrderByEnum.Desc,
          },
        }),
      ]);

      const merged: BodyCompositionRow[] = [];
      const maxLen = Math.max(
        weightRes.data.length,
        bodyFatRes.data.length,
        bmiRes.data.length,
        leanMassRes.data.length,
      );

      for (let i = 0; i < maxLen; i++) {
        const weight = weightRes.data[i];
        const bodyFatPercentage = bodyFatRes.data[i];
        const bmi = bmiRes.data[i];
        const leanBodyMass = leanMassRes.data[i];
        if (!weight && !bodyFatPercentage && !bmi && !leanBodyMass) continue;
        const recordedAt = new Date(
          Math.max(
            weight ? new Date(weight.startDate).getTime() : 0,
            bodyFatPercentage
              ? new Date(bodyFatPercentage.startDate).getTime()
              : 0,
            bmi ? new Date(bmi.startDate).getTime() : 0,
            leanBodyMass ? new Date(leanBodyMass.startDate).getTime() : 0,
          ),
        );
        merged.push({
          weight,
          bodyFatPercentage,
          bmi,
          leanBodyMass,
          recordedAt,
        });
      }

      const total = Math.max(
        weightRes.pagination.total ?? 0,
        bodyFatRes.pagination.total ?? 0,
        bmiRes.pagination.total ?? 0,
        leanMassRes.pagination.total ?? 0,
      );
      return { data: merged, total };
    },
    [patientId],
  );

  const {
    data,
    total,
    page,
    rowsPerPage,
    rowsPerPageOptions,
    isLoading,
    onPageChange,
    onRowsPerPageChange,
  } = usePatientActivity<BodyCompositionRow>({
    patientId,
    queryKey: BODY_COMPOSITION_QUERY_KEY,
    initialPageSize: 10,
    fetchPage,
  });

  const chartConfigs = [
    {
      metricCode: MetricValuesPaginationRequestDtoMetricCodeEnum.Weight,
      title: "Weight",
      unit: "lb",
      color: chartColors[0],
    },
    {
      metricCode:
        MetricValuesPaginationRequestDtoMetricCodeEnum.BodyFatPercentage,
      title: "Body Fat Percentage",
      unit: "%",
      color: chartColors[1],
    },
    {
      metricCode: MetricValuesPaginationRequestDtoMetricCodeEnum.BodyMassIndex,
      title: "BMI",
      unit: "",
      color: chartColors[2],
    },
    {
      metricCode: MetricValuesPaginationRequestDtoMetricCodeEnum.LeanBodyMass,
      title: "Lean Body Mass",
      unit: "lb",
      color: chartColors[3],
    },
  ];

  const ranges: TimeRange[] = ["1D", "1W", "1M", "6M", "1Y"];

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 3,
        height: "calc(100vh - 340px)",
      }}
    >
      <TimeRangeSelector
        ranges={ranges}
        selectedTimeRange={selectedTimeRange}
        onTimeRangeChange={setSelectedTimeRange}
      />

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: 2,
        }}
      >
        {chartConfigs.map(({ metricCode, title, unit, color }, idx) => (
          <Box key={metricCode} sx={{ height: 320 }}>
            <BodyCompositionChart
              patientId={patientId}
              metricCode={metricCode}
              title={title}
              unit={unit}
              color={color}
              selectedTimeRange={selectedTimeRange}
            />
          </Box>
        ))}
      </Box>

      <CustomTable<BodyCompositionRow>
        columns={columns}
        loadingMessage="Loading body composition data..."
        emptyStateContent={
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
            }}
          >
            <FitnessCenterIcon
              sx={{ color: "text.disabled", fontSize: 48, opacity: 0.5 }}
            />
            <Typography
              variant="h6"
              sx={{ color: "text.secondary", fontWeight: 500 }}
            >
              No body composition data available
            </Typography>
          </Box>
        }
        getRowKey={(row) => row.recordedAt.getTime().toString()}
        tableContainerSx={{
          height: "calc(100vh - 500px)",
          minHeight: "600px",
        }}
        data={data}
        totalCount={total}
        page={page}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={rowsPerPageOptions}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
        isLoading={isLoading}
      />
    </Box>
  );
};

export default BodyCompositionTabContent;
